import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/services/apiClient';

/**
 * Decode the v5 tag-icon proxy response into a blob URL. The endpoint
 * returns either a raw image Blob (e.g. when an interceptor reshapes
 * responses) or the `{contentType, data: base64}` JSON envelope used
 * by the patient-photo proxy. Both shapes resolve to a cached
 * `URL.createObjectURL(...)` so the image can be rendered through
 * the auth-protected endpoint without exposing bearer tokens in
 * `<img src>` attributes.
 */
async function fetchTagIconBlobUrl(tagsCustomId: number): Promise<string | null> {
  const response = await apiClient.get<{ contentType?: string; data?: string } | Blob>(
    `/tags-custom/${tagsCustomId}/icon`,
    {
      headers: { Accept: 'image/*' },
    },
  );

  const raw = response.data;

  if (raw instanceof Blob) {
    if (raw.size === 0) return null;
    const blobType = String(raw.type ?? '').toLowerCase();
    if (blobType !== '' && !blobType.startsWith('image/')) return null;
    return URL.createObjectURL(raw);
  }

  if (raw && typeof raw === 'object' && typeof raw.data === 'string' && typeof raw.contentType === 'string') {
    try {
      const bytes = Uint8Array.from(atob(raw.data), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: raw.contentType });
      if (blob.size === 0) return null;
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Fetches a tag's icon image via the v5 → v4 proxy and exposes it as a
 * cached object URL. Mirrors {@link usePatientPhoto} so the same React
 * Query cache + blob lifecycle apply: revisiting the same tag id reuses
 * the previously decoded blob instead of refetching, and the URL is
 * freed when the cache entry is garbage collected.
 *
 * The hook intentionally takes a nullable id so callers can short-circuit
 * the fetch when a tag has no icon assigned (without violating the
 * rule-of-hooks). When `tagsCustomId` is null, no request is fired.
 *
 * Tag-icon definitions rarely change (typically only when an admin
 * uploads a replacement), so the stale window is set substantially
 * longer than for patient photos.
 */
export function useTagIcon(tagsCustomId: number | null): string | undefined {
  const token = useAuthStore((s) => s.token);
  const accountId = useAuthStore((s) => s.accountId);

  const query = useQuery({
    queryKey: ['tag-icon', tagsCustomId, accountId],
    queryFn: () => fetchTagIconBlobUrl(tagsCustomId!),
    enabled: !!tagsCustomId && !!token,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: false,
  });

  return query.data ?? undefined;
}
